import React from 'react'
import styled from 'styled-components'
import {sx, SxProp} from '../..'

export type VisuallyHiddenProps = React.ComponentPropsWithRef<'span'> & {
  children?: React.ReactNode
  isVisible?: boolean
}

/**
 * The VisuallyHidden component supports text that is visually hidden from the UI, but is read by screen readers.
 *
 * Use with icons, buttons without text, labels, and other parts of the UI that need to provide instructions that isn't
 * immediately clear to people using screen readers or assistive technologies.
 */
const VisuallyHidden = ({children, ...rest}: VisuallyHiddenProps & SxProp) => {
  const [forceShow, setForceShow] = React.useState(false)

  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // the reveal when debugging trick doesn't really work in iFrames
      const handleKeyDown = (ev: KeyboardEvent): void => {
        if (ev.key === 'Alt') {
          setForceShow(true)
        }
      }
      const handleKeyUp = () => {
        setForceShow(false)
      }
      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keydown', handleKeyUp)
      }
    }
  }, [])
  if (forceShow) {
    return <Wrapper {...rest}>{children}</Wrapper>
  }
  return <Wrapper {...rest}>{children}</Wrapper>
}

export default VisuallyHidden

const Wrapper = styled.span<VisuallyHiddenProps & SxProp>`
  ${({isVisible = false}) => {
    if (isVisible) {
      return sx
    }
    return `position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  `
  }}
`
